import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Star, BellSimple, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { authFetch } from "../context/AuthContext";
import { fileUrl } from "./PhotoGallery";
import { toast } from "sonner";

const SIGN_GLYPH = {
    Aries: "\u2648", Taurus: "\u2649", Gemini: "\u264A", Cancer: "\u264B",
    Leo: "\u264C", Virgo: "\u264D", Libra: "\u264E", Scorpio: "\u264F",
    Sagittarius: "\u2650", Capricorn: "\u2651", Aquarius: "\u2652", Pisces: "\u2653",
};

const NOTIFY_KEY = "soulstar_daily_notify_shown";

export default function TopMatchToday() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notifPerm, setNotifPerm] = useState(
        typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported",
    );

    // Fire a browser notification once per day, only if user opted-in
    const maybeNotify = useCallback((match, dateKey) => {
        if (!("Notification" in window)) return;
        if (Notification.permission !== "granted") return;
        const already = localStorage.getItem(NOTIFY_KEY);
        if (already === dateKey) return;
        try {
            new Notification("Your most aligned soul today", {
                body: `${match.name} \u00b7 ${match.compatibility.overall}% cosmic match`,
                tag: `soulstar-daily-${dateKey}`,
                icon: "/logo-clean.png",
                silent: false,
            });
            localStorage.setItem(NOTIFY_KEY, dateKey);
        } catch { /* ignore quota / permission edge cases */ }
    }, []);

    useEffect(() => {
        authFetch("get", "/matches/top-today")
            .then((r) => {
                setData(r.data);
                if (r.data?.match) maybeNotify(r.data.match, r.data.date);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [maybeNotify]);

    const enableNotifications = async () => {
        if (!("Notification" in window)) {
            toast.error("Notifications aren't supported on this browser");
            return;
        }
        try {
            const p = await Notification.requestPermission();
            setNotifPerm(p);
            if (p === "granted") {
                toast.success("Daily cosmic ping enabled");
                if (data?.match) maybeNotify(data.match, data.date);
            } else if (p === "denied") {
                toast.error("Notifications blocked - update site permissions");
            }
        } catch {
            toast.error("Could not enable notifications");
        }
    };

    if (loading) return null;
    if (!data?.match) return null;

    const m = data.match;
    const showEnableCta = notifPerm === "default";

    return (
        <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            data-testid="top-match-today"
        >
            <div className="glass-gold p-6 sm:p-7 relative overflow-hidden">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-[#E6C387]/25 to-transparent blur-2xl pointer-events-none" />
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4 relative">
                    <div>
                        <p className="label-tracking text-gold flex items-center gap-2">
                            <Sparkle size={12} weight="duotone" /> Today's Alignment
                        </p>
                        <h2 className="font-serif text-2xl sm:text-3xl mt-1">Your most aligned soul today</h2>
                    </div>
                    {showEnableCta && (
                        <button
                            onClick={enableNotifications}
                            className="text-xs label-tracking text-gold border border-gold/30 rounded-full px-3 py-1.5 hover:bg-gold/10 transition-colors inline-flex items-center gap-1.5"
                            data-testid="enable-daily-notify"
                        >
                            <BellSimple size={12} weight="duotone" /> Notify me daily
                        </button>
                    )}
                </div>

                <Link
                    to={`/u/${m.user_id}`}
                    className="flex items-center gap-4 sm:gap-5 group relative"
                    data-testid="top-match-today-link"
                >
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white/5 border border-gold/40 overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                        {m.photos?.[0] ? (
                            <img src={fileUrl(m.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-4xl text-gold font-serif">{SIGN_GLYPH[m.sun]}</span>
                        )}
                        {m.verified && (
                            <span
                                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0A0A1A] border border-gold flex items-center justify-center"
                                title="Verified"
                            >
                                <ShieldCheck size={12} weight="fill" className="text-gold" />
                            </span>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="font-serif text-2xl sm:text-3xl truncate group-hover:text-gold transition-colors" data-testid="top-match-name">
                                {m.name}{m.age ? `, ${m.age}` : ""}
                            </h3>
                        </div>
                        <p className="text-xs sm:text-sm text-muted mt-1 truncate">
                            {SIGN_GLYPH[m.sun]} {m.sun} · ☽ {m.moon} · ↑ {m.rising}
                        </p>
                        <p className="text-xs sm:text-sm italic font-serif text-muted mt-2 line-clamp-2">
                            {m.compatibility.sun_compat?.description}
                        </p>
                    </div>
                    <div className="text-center flex-shrink-0">
                        <div className="relative w-16 h-16 sm:w-20 sm:h-20">
                            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="44" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                                <circle
                                    cx="50" cy="50" r="44" fill="none" stroke="#E6C387" strokeWidth="6"
                                    strokeDasharray={`${m.compatibility.overall * 2.76} 999`} strokeLinecap="round"
                                />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="font-serif text-2xl sm:text-3xl text-gold leading-none" data-testid="top-match-score">
                                    {m.compatibility.overall}
                                </span>
                                <Star size={10} weight="fill" className="text-gold mt-0.5" />
                            </div>
                        </div>
                    </div>
                </Link>
            </div>
        </motion.section>
    );
}
